import React from "react";
import { Orientation, showTrussName, WallOrientation } from "./Summary";
import { capitalize, hasGableLeanto } from "../../../redux/reducers/utils";
import CurrencyFormat from "react-currency-format";
import { upperCaseFirst } from "../../../redux/reducers/building_pricing/pricingReducer";
import { CrossBracingSummary, ExtraOptionsSummary } from "./ExtraOptionsSummary";
import { LEAN_TYPE_NUM_TO_STR } from "../../../Constants/constant";

function PriceDisplay({ value }) {
	if (value == null) return null;
	return <CurrencyFormat fixedDecimalScale="2" decimalSeparator="." decimalScale="2" value={value} displayType={"text"} thousandSeparator={true} prefix={"$"} />;
}

function SummaryRow({ label, children, price }) {
	return (
		<div className="summary-row d-flex justify-content-between">
			<div className="summary-label">
				{label}
				{children}
			</div>
			{price != null && (
				<div className="summary-info">
					<aside className="summary-info-right">
						<PriceDisplay value={price} />
					</aside>
				</div>
			)}
		</div>
	);
}

function resolveWallText(wallKey, typeWallName) {
	const panelExclusions = ["open", "close", "one_fourth_close", "three_fourth_close", "half_close"];
	return !panelExclusions.includes(wallKey) ? wallKey + " Panel" : typeWallName[wallKey];
}

function EndsSummary({ value, const_var, params }) {
	const frontClosed = const_var.TypeWallName[value.front_wall]?.toLowerCase() === "closed";
	const backClosed = const_var.TypeWallName[value.back_wall]?.toLowerCase() === "closed";

	if (frontClosed && backClosed) {
		return <SummaryRow label={<span className="summary-label-title">{"Ends - Fully Enclosed"}</span>} price={parseFloat(value?.front_wall_price ?? 0) + parseFloat(value?.back_wall_price ?? 0)} />;
	}

	const showBack =
		(value.leanto_type == 1 && params.p_l_w !== "Close") ||
		(value.leanto_type == 2 && params.p_r_w !== "Close") ||
		(value.leanto_type == 3 && params.p_f_w !== "Close") ||
		(value.leanto_type == 4 && params.p_b_w !== "Close") ||
		value.leanto_style == "standard";

	const backWallKey = value.utility_length != undefined && value?.utility_position != undefined && value.utility_position == 0 ? "close" : value.back_wall;

	return (
		<>
			<SummaryRow
				label={
					<>
						<span className="summary-label-title">{"Front End - "}</span>
						<span className="dark-span">{const_var.TypeWallName[value.front_wall]}</span>
					</>
				}
				price={value?.front_wall_price}
			/>

			{showBack && (
				<SummaryRow
					label={
						<>
							<span className="summary-label-title">{"Back End - "}</span>
							<span className="dark-span">{const_var.TypeWallName[backWallKey]}</span>
						</>
					}
					price={value?.back_wall_price == undefined ? 0 : value?.back_wall_price}
				/>
			)}
		</>
	);
}

function SidesSummary({ value, const_var }) {
	const sideClosed = value?.side_wall && const_var?.TypeWallName?.[value.side_wall]?.toLowerCase() === "closed";
	const rightClosed = value?.right_wall && const_var?.TypeWallName?.[value.right_wall]?.toLowerCase() === "closed";

	if (sideClosed && rightClosed) {
		return <SummaryRow label={<span className="summary-label-title">{"Sides - Fully Enclosed"}</span>} price={parseFloat(value?.side_wall_price ?? 0) + parseFloat(value?.right_wall_price ?? 0)} />;
	}

	const isGable = value.leanto_style != undefined && value.leanto_style == "gable";
	const sideLabel = isGable ? "Left" : "Side Wall";
	const sideWallText = resolveWallText(value.side_wall, const_var.TypeWallName);
	const rightWallText = resolveWallText(value.right_wall, const_var.TypeWallName);

	return (
		<>
			<SummaryRow
				label={
					<>
						<span className="summary-label-title">{sideLabel}</span>
						{" - "}
						<span className="dark-span">{sideWallText}</span>
					</>
				}
				price={value?.side_wall_price}
			/>

			{isGable && (
				<SummaryRow
					label={
						<>
							<span className="summary-label-title">Right</span>
							{" - "}
							<span className="dark-span">{rightWallText}</span>
						</>
					}
					price={value?.right_wall_price}
				/>
			)}
		</>
	);
}

export default function LeantoSummary({ summaryViewData, const_var, params, side_close_cost_check, extraItemLabel, compareParamData }) {
	return (
		<div>
			{Object.entries(summaryViewData.leanto).map(([key, value], index) => {
				let label = capitalize(LEAN_TYPE_NUM_TO_STR[value.leanto_type]) + " Lean-to";
				let isAddedBuilding = false;
				if (hasGableLeanto(LEAN_TYPE_NUM_TO_STR[value.leanto_type])) {
					isAddedBuilding = true;
					label = `${capitalize(LEAN_TYPE_NUM_TO_STR[value.leanto_type])} (Building ${index + 2}) `;
				}
				const hasAnyWall = value.back_wall !== "open" || value.front_wall !== "open" || value.side_wall !== "open" || value?.right_wall !== "open" || value.utility_length > 0;

				return (
					<div key={key}>
						<hr />
						<div className="section-title-wrapper ">
							<h6 className="section-title mb-0">{label}</h6>
						</div>

						<div className="highlight-with-bg">
							<SummaryRow
								label={
									<>
										<span className="summary-label-title">Size (WxL)</span>
										{" - "}
										<span className="dark-span">
											{value.width} x {value.length} Feet
										</span>
										{isAddedBuilding && params?.frame_length != 0 && (
											<span>
												{" "}
												Roof Length {" - "}
												{value.length + params.frame_length}'
											</span>
										)}
										{value.leanto_type == 1 || value.leanto_type == 2
											? value.porch != undefined && <sapn> &nbsp; | &nbsp; Wrapped length {value.porch.length + " '"} </sapn>
											: value.wrappedLength != 0 && <sapn> &nbsp; | &nbsp; Wrapped length {value.wrappedLength + " '"} </sapn>}
									</>
								}
								price={value?.basic_price}
							/>
							<SummaryRow
								label={
									<>
										<span className="summary-label-title">Height</span>
										{" - "}
										<span className="dark-span">
											{value.height}
											{"'H "}
										</span>
										<span className="f-11">
											{value.lean_has_double_leg
												? !value.lean_has_double_leg_name.includes("Baserails")
													? summaryViewData?.manufacturer == 122 && value.lean_has_double_leg_name?.toLowerCase()?.includes("double")
														? `${value.lean_has_double_leg_name} / Double Baserail`
														: value.lean_has_double_leg_name.toLowerCase().includes("double")
															? "Double Legs"
															: value.lean_has_double_leg_name + " Baserail"
													: value.lean_has_double_leg_name.toLowerCase().includes("double")
														? "Double Legs"
														: value.lean_has_double_leg_name + " Baserail"
												: ""}
										</span>
									</>
								}
								price={value?.height_price}
							/>
							<SummaryRow
								label={
									<>
										<span className="summary-label-title">Roof Pitch</span>
										{" - "}
										<span className="dark-span">{value?.roof_pitch_custom_name ?? value.roof_pitch}</span>
									</>
								}
								price={value?.roof_pitch_price}
							/>
							{isAddedBuilding && showTrussName(value)}

							<SummaryRow
								label={
									<>
										<span className="summary-label-title">Alignment</span>
										{" - "}
										<span className="dark-span">{upperCaseFirst(value.alignment)}</span>
									</>
								}
							/>

							<EndsSummary value={value} const_var={const_var} params={params} />
							<SidesSummary value={value} const_var={const_var} />

							{value.utility_length != undefined && (
								<SummaryRow
									label={
										<>
											<span className="summary-label-title">{value?.utility_position != 0 ? "Storage (Position-" + Math.round(value.utility_position) + "' from Back)" : "Utility Storage"}</span>
											{" - "}
											<span className="dark-span">{value.utility_length} Feet</span>
										</>
									}
									price={value?.utility_price}
								/>
							)}
						</div>
						{value.leanto_style == "gable" && value?.extra_items?.length > 0 && <CrossBracingSummary options={value?.extra_items} leanType={LEAN_TYPE_NUM_TO_STR[value.leanto_type]} mainData={value} />}
						{value?.extra_items?.length > 0 && (
							<>
								<ExtraOptionsSummary
									options={value?.extra_items}
									extraItemLabel={extraItemLabel}
									params={params}
									compareParamData={compareParamData}
									buildingType={LEAN_TYPE_NUM_TO_STR[value.leanto_type]}
									extraAddOnArray={value.extraAddOnItemsArray}
								/>
							</>
						)}

						{(value.back_wall !== "open" || value.front_wall !== "open" || value.side_wall !== "open" || value?.right_wall !== "open" || value.utility_length > 0) && (
							<WallOrientation side_close_cost_check={side_close_cost_check} summaryViewData={summaryViewData} params={params} />
						)}
					</div>
				);
			})}
		</div>
	);
}
